import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CropCenterEditor from "@/components/CropCenterEditor";
import type { CropCenterKeyframe } from "@/hooks/use-auto-save-crop-keyframes";
import {
  applySceneCropFraming,
  countSceneCropSections,
  removeSceneCropSection,
  resolveSceneCropTarget,
} from "@/lib/crop/sceneCropFraming";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keyframes: CropCenterKeyframe[];
  /** The clip's scenes (source time). An edit only changes the one under the playhead. */
  scenes: ReadonlyArray<{ startTime: number; endTime: number }>;
  onChange: (next: CropCenterKeyframe[]) => void;
  videoUrl?: string;
  sourceAspectRatio?: number;
  currentTime: number;
}

const ReframeModal: React.FC<Props> = ({
  open,
  onOpenChange,
  keyframes,
  scenes,
  onChange,
  videoUrl,
  sourceAspectRatio,
  currentTime,
}) => {
  const { t } = useTranslation();

  // The scene under the playhead, limited to the keyframe framing it. A
  // keyframe is a detected camera shot and often spans several scenes;
  // editing it whole used to reframe all of them.
  const target = useMemo(
    () => resolveSceneCropTarget(keyframes, scenes, currentTime),
    [keyframes, scenes, currentTime],
  );
  const canDelete = !!target && countSceneCropSections(keyframes, target.scene) > 1;

  const updateActive = (next: CropCenterKeyframe) => {
    if (!target) return;
    onChange(applySceneCropFraming({
      keyframes,
      scenes,
      range: { start: target.start, end: target.end },
      framing: { x: next.x, y: next.y, zoom: next.zoom },
    }));
  };

  // Merges this section into its neighbor inside the same scene only.
  const handleDelete = () => {
    if (!target || !canDelete) return;
    onChange(removeSceneCropSection({
      keyframes,
      scenes,
      scene: target.scene,
      range: { start: target.start, end: target.end },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-fit">
        <DialogHeader>
          <DialogTitle>{t("reframeModal.title")}</DialogTitle>
        </DialogHeader>

        {target ? (
          <CropCenterEditor
            keyframe={target.keyframe}
            videoUrl={videoUrl}
            sourceAspectRatio={sourceAspectRatio}
            canDelete={canDelete}
            onChange={updateActive}
            onDelete={handleDelete}
            frameWidth={480}
            frameTime={currentTime}
            previewStartTime={target.start}
            previewEndTime={target.end}
          />
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t(keyframes.length ? "reframeModal.outsideScene" : "reframeModal.empty")}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReframeModal;
